const MIN_SUBSTRING_LENGTH = 4;
const MIN_COLLAPSED_LENGTH = 4;

export function normalizeSearchText(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function maxEditDistance(length: number): number {
  if (length <= 2) return 0;
  if (length <= 5) return 1;
  return 2;
}

/** Damerau-Levenshtein so a single adjacent transposition counts as one edit. */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
      }
    }
  }

  return dp[m][n];
}

function tokenMatches(queryToken: string, hayToken: string): boolean {
  if (hayToken.startsWith(queryToken)) return true;
  if (queryToken.length >= MIN_SUBSTRING_LENGTH && hayToken.includes(queryToken)) return true;

  const max = maxEditDistance(queryToken.length);
  if (max === 0) return false;
  if (Math.abs(hayToken.length - queryToken.length) > max) return false;
  return editDistance(queryToken, hayToken) <= max;
}

/**
 * True when every query word fuzzy-matches the combined venue fields.
 * An empty query matches everything.
 */
export function matchesFuzzyQuery(
  query: string,
  fields: Array<string | null | undefined>,
): boolean {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  const haystack = normalizeSearchText(fields.filter((field) => !!field).join(' '));
  if (!haystack) return false;
  if (haystack.includes(normalizedQuery)) return true;

  const queryTokens = normalizedQuery.split(' ');
  const hayTokens = haystack.split(' ');
  const collapsedQuery = queryTokens.join('');

  if (
    collapsedQuery.length >= MIN_COLLAPSED_LENGTH &&
    collapsedQuery !== normalizedQuery &&
    hayTokens.join('').includes(collapsedQuery)
  ) {
    return true;
  }

  return queryTokens.every((queryToken) =>
    hayTokens.some((hayToken) => tokenMatches(queryToken, hayToken)),
  );
}

/**
 * True when a non-empty search still has venue pages to fetch.
 * `loadingMore` blocks a second fetch. `loadFailed` blocks an automatic retry
 * of the same failed search until the query changes.
 */
export function shouldLoadRemainingVenuesForSearch(
  query: string,
  hasMore: boolean,
  loadingMore: boolean,
  loadFailed = false,
): boolean {
  return query.trim().length > 0 && hasMore && !loadingMore && !loadFailed;
}

/**
 * True when the table must not say "no venues match" yet, because this search
 * is still paging through venues that are not in memory.
 */
export function isVenueSearchStillLoading(
  query: string,
  hasMore: boolean,
  loading: boolean,
  loadFailed: boolean,
): boolean {
  return query.trim().length > 0 && !loadFailed && (loading || hasMore);
}
